import { ApolloError, BaseMutationOptions, BaseQueryOptions } from '@apollo/client';
import { create } from 'zustand';
import { resolveValue, toast as hotToast, Toaster as HotToaster, ToastOptions } from 'react-hot-toast';
import { Theme, useMediaQuery } from '@mildang/mui';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import {
  DefaultToastOptions,
  Renderable,
  Toast,
  ToastType,
  ValueOrFunction,
} from 'react-hot-toast/dist/core/types';
import { ReactNode } from 'react';
import Alert, { AlertProps } from '@mildang/design-system-legacy/esm/components/molecules/Alert';
import { apolloErrorParser } from '@shared/utils/errorParser';
import { isProject } from '@shared/constants/projectTarget';

export type CustomToastType = ToastType | 'apolloError' | 'info' | 'basic';

const ToastTypeToSnackBar: {
  [key in CustomToastType]: AlertProps['severity'] | undefined;
} = {
  success: 'success',
  error: 'error',
  apolloError: 'error',
  info: 'info',
  loading: 'info',
  basic: 'basic',
  blank: undefined,
  custom: undefined,
};

export type CustomToastOptions = ToastOptions & {
  title?: ReactNode;
  infinite?: boolean;
  severity?: 'warning' | 'basic';
  lineClamp?: number;
  onClose?: () => void;
} & (
    | {
        action?: ReactNode;
        isClosable?: false;
      }
    | {
        action?: ReactNode | ((params: { onClose: () => void }) => ReactNode);
        isClosable: true;
      }
  );
export type CustomToast = Toast & {
  title?: ReactNode;
  action?: ReactNode | ((params: { onClose?: () => void }) => ReactNode);
  isClosable?: boolean;
  infinite?: boolean;
  severity?: 'warning' | 'basic';
  lineClamp?: number;
  onClose?: () => void;
};

declare type Message = ValueOrFunction<Renderable, Toast>;
declare type ToastHandler = (message: Message, options?: CustomToastOptions) => string;
declare type ApolloErrorToastHandler = (
  error: ApolloError,
  options?: CustomToastOptions & BaseMutationOptions & BaseQueryOptions,
) => string;

interface ToastCaller {
  (message: Message, opts?: Partial<CustomToastOptions> | undefined): string;
  error: ToastHandler;
  apolloError: ApolloErrorToastHandler;
  success: ToastHandler;
  basic: ToastHandler;
  info: ToastHandler;
  warning: ToastHandler;
  blank: ToastHandler;
  loading: ToastHandler;
  custom: ToastHandler;
  dismiss(toastId?: string | undefined): void;
  remove(toastId?: string | undefined): void;
  promise<T>(
    promise: Promise<T>,
    msgs: {
      loading: Renderable;
      success: ValueOrFunction<Renderable, T>;
      error: ValueOrFunction<Renderable, any>;
    },
    opts?: DefaultToastOptions | undefined,
  ): Promise<T>;
}
export const toast = hotToast as ToastCaller;
toast.apolloError = (error, options) => {
  return toast.error('', {
    ...options,
    // @ts-ignore
    type: 'apolloError',
    apolloError: error,
  });
};

toast.info = (message, options) => {
  return toast.loading(message, options);
};

toast.warning = (message, options) => {
  return toast.success(message, {
    ...options,
    severity: 'warning',
  });
};

toast.basic = (message, options) => {
  return toast.success(message, {
    ...options,
    severity: 'basic',
  });
};

export const useToasterStore = create<{
  toasterConfig: { containerStyle?: React.CSSProperties };
  setToasterConfig: (config: { containerStyle?: React.CSSProperties }) => void;
}>((set) => ({
  toasterConfig: {},
  setToasterConfig: (config) =>
    set((prev) => ({
      ...prev,
      toasterConfig: {
        ...config,
      },
    })),
}));

const Toaster = () => {
  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const toasterConfig = useToasterStore((state) => state.toasterConfig);
  const { i18n } = useLingui();

  return (
    <HotToaster
      position={isSmDown ? 'bottom-center' : 'top-right'}
      toastOptions={{
        duration: 2000,
      }}
      {...toasterConfig}
    >
      {(toastConfig: CustomToast) => {
        let title: ReactNode = '';
        let description: ReactNode = '';

        // @ts-ignore
        if (toastConfig.type === 'apolloError') {
          try {
            const apolloErrorResult = apolloErrorParser(
              i18n,
              // @ts-ignore
              toastConfig.apolloError as any,
            );
            title = apolloErrorResult.title;
            description = apolloErrorResult.desc;

            if (!title && !description && isProject('schoolpt')) {
              // @ts-ignore
              const error = toastConfig.apolloError;

              switch (error.graphQLErrors?.[0].extensions?.code) {
                case 'VERIFICATION_FAIL':
                  title = t(i18n)`인증번호가 일치하지 않습니다.`;
                case 'ALREADY_JOINED_EMAIL':
                  title = t(i18n)`이미 존재하는 이메일 입니다.`;
                case 'NOT_FOUND':
                  title = t(i18n)`존재하지 않는 기관입니다.`;
                default:
                  title = t(i18n)`알 수 없는 에러가 발생하였습니다.`;
              }
            }
          } catch (error) {}
        } else {
          title = toastConfig.title ?? '';
          const message = resolveValue(toastConfig.message, toastConfig);
          description = message;
        }
        const severity = toastConfig.severity ?? ToastTypeToSnackBar[toastConfig.type];
        const onClose = toastConfig.isClosable
          ? () => {
              toast.dismiss(toastConfig.id);
              toastConfig.onClose?.();
            }
          : undefined;

        return (
          <Alert
            {...toastConfig.ariaProps}
            // @ts-ignore
            className={toastConfig.className}
            severity={severity}
            title={title}
            message={description}
            action={
              typeof toastConfig.action === 'function' ? toastConfig.action({ onClose }) : toastConfig.action
            }
            onClose={onClose}
            infinite={toastConfig.duration === Infinity || toastConfig.infinite}
          />
        );
      }}
    </HotToaster>
  );
};

export default Toaster;
